/**
 * Storage Utilities for Autocomplete Functionality
 * 
 * Provides helper functions for managing localStorage data for autocomplete suggestions.
 * These utilities help persist user-entered values for better user experience.
 */

/**
 * Get stored suggestions from localStorage
 * @param {string} key - The localStorage key to retrieve data from
 * @returns {Array} Array of stored suggestions or empty array if none found
 */
export const getStoredSuggestions = (key) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error(`Error reading ${key} from localStorage:`, error);
    return [];
  }
};

/**
 * Save a suggestion to localStorage
 * @param {string} key - The localStorage key to save data to
 * @param {string} value - The value to save
 * @param {number} maxItems - Maximum number of items to keep (default: 50)
 */
export const saveToStorage = (key, value, maxItems = 50) => {
  try {
    if (!value || !value.trim()) return;
    
    const existing = getStoredSuggestions(key);
    const trimmedValue = value.trim();
    
    // Don't add if already exists (case-insensitive)
    const exists = existing.some(item => 
      item.toLowerCase() === trimmedValue.toLowerCase()
    );
    
    if (!exists) {
      const updated = [trimmedValue, ...existing].slice(0, maxItems);
      localStorage.setItem(key, JSON.stringify(updated));
    }
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error);
  }
};

/**
 * Remove a suggestion from localStorage
 * @param {string} key - The localStorage key to remove data from
 * @param {string} value - The value to remove
 */
export const removeFromStorage = (key, value) => {
  try {
    if (!value) return;
    
    const existing = getStoredSuggestions(key);
    const updated = existing.filter(item => 
      item.toLowerCase() !== value.toLowerCase()
    );
    
    localStorage.setItem(key, JSON.stringify(updated));
  } catch (error) {
    console.error(`Error removing ${value} from ${key} in localStorage:`, error);
  }
};

/**
 * Clear all suggestions for a specific key
 * @param {string} key - The localStorage key to clear
 */
export const clearStoredSuggestions = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error clearing ${key} from localStorage:`, error);
  }
};

/**
 * Get all autocomplete storage keys
 * @returns {Object} Object containing all storage keys used by the application
 */
export const STORAGE_KEYS = {
  // Allergies
  ALLERGY_MEDICINES: 'autocomplete_allergy_medicines',
  ALLERGY_FOOD: 'autocomplete_allergy_food',
  ALLERGY_OTHER: 'autocomplete_allergy_other',
  
  // Medical data
  MEDICINES: 'autocomplete_medicines',
  DIAGNOSIS: 'autocomplete_diagnosis',
  COMPLAINTS: 'autocomplete_complaints',
  SEVERITY: 'autocomplete_severity',
  ADVICE: 'autocomplete_advice',
  CLINICAL_FINDINGS: 'autocomplete_clinical_findings',
  
  // Investigations
  INVESTIGATIONS: 'autocomplete_investigations',
  
  // Referrals
  REFERRED_DOCTORS: 'autocomplete_referred_doctors',
  SPECIALITIES: 'autocomplete_specialities'
  ,
  // Frequencies saved for doctor recommendations
  FREQUENCIES: 'doctor_recommendation_frequencies'
};

/**
 * Get parameters data from localStorage
 * @returns {Object|null} parameters data object or null if not found
 */
export const getparametersFromLocalStorage = () => {
  try {
    const stored = localStorage.getItem('unsyncedparameters');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error reading parameters from localStorage:', error);
    return null;
  }
};

/**
 * Save text inputs data to localStorage
 * @param {Object} data - The text inputs data to save
 */
export const saveTextInputsData = (data) => {
  try {
    localStorage.setItem('textInputsData', JSON.stringify(data));
  } catch (error) {
    console.error('Error saving text inputs data to localStorage:', error);
  }
};

/**
 * Get text inputs data from localStorage
 * @returns {Object} Text inputs data object or empty object if not found
 */
export const getTextInputsData = () => {
  try {
    const stored = localStorage.getItem('textInputsData');
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading text inputs data from localStorage:', error);
    return {};
  }
};

/**
 * Initialize default suggestions for better user experience
 * This function can be called on app startup to populate common suggestions
 */
export const initializeDefaultSuggestions = () => {
  const defaults = {
    [STORAGE_KEYS.ALLERGY_MEDICINES]: [
      'Penicillin', 'Aspirin', 'Ibuprofen', 'Sulfa drugs', 'Codeine'
    ],
    [STORAGE_KEYS.ALLERGY_FOOD]: [
      'Peanuts', 'Shellfish', 'Eggs', 'Milk', 'Wheat', 'Soy'
    ],
    [STORAGE_KEYS.ALLERGY_OTHER]: [
      'Dust', 'Pollen', 'Animal dander', 'Latex', 'Mold'
    ],
    [STORAGE_KEYS.COMPLAINTS]: [
      'Headache', 'Fever', 'Cough', 'Sore throat', 'Body ache', 'Fatigue'
    ],
    [STORAGE_KEYS.DIAGNOSIS]: [
      'Common cold', 'Flu', 'Migraine', 'Hypertension', 'Diabetes'
    ]
  };
  
  Object.entries(defaults).forEach(([key, suggestions]) => {
    const existing = getStoredSuggestions(key);
    if (existing.length === 0) {
      localStorage.setItem(key, JSON.stringify(suggestions));
    }
  });
};